/*
 * Trust — Plane III (§15–§23): PKI, participant registry, RFC 6962 Merkle
 * transparency log, federation cross-signing.
 *
 * WIRING STATE: the STH, participant registry, and inclusion-proof panels are
 * real, live reads against services/trust_plane/app.py, same-origin through
 * the console proxy:
 *   - Signed-Tree-Head (STH) panel        → trust.getSignedTreeHead()
 *   - Participant registry                 → trust.listParticipants()
 *   - Inclusion-proof VIEWER (leaf hash →   → trust.getInclusionProof(leaf)
 *     RFC 6962 §2.1.1 audit path)
 * A genuinely unreachable service still renders the honest offline panel; a
 * live-but-empty answer (no anchor published yet) renders its own honest
 * empty state, never an error. Federation cross-signing has no backend route
 * yet, so that one panel stays a first-class offline shell.
 *
 * The inclusion-proof renderer is a pure component: given a proof object it
 * draws the sibling-hash ladder up to the anchor it climbs to. It does not
 * recompute SHA-256 in the browser — the log is the authority; the proof's
 * job is to let an auditor replay it. It never invents a path.
 */

import { useCallback, useEffect, useState } from "react";
import {
  trust,
  isPlaneOffline,
  isMeshApiError,
  type PlaneOfflineError,
  type RegistrySnapshot,
  type SignedTreeHead,
  type InclusionProofResult,
} from "../api/client";
import { usePlaneHealth, type PlaneHealthState } from "../api/metrics";
import { PlaneState } from "../components/PlaneState";
import { VerifyBadge } from "../components/VerifyBadge";
import { Hash } from "../components/Hash";
import { ProvabilityDrawer } from "../components/ProvabilityDrawer";
import { SpecRef } from "../components/SpecRef";
import type { Claim, Role, VerifyState } from "../api/types";

/* ------------------------------------------------------------------ */
/* The wire shapes below (SignedTreeHead / RegistryParticipant /       */
/* InclusionProofResult) are imported from api/client.ts, where they   */
/* mirror services/trust_plane/app.py exactly — never guessed here.    */
/*                                                                      */
/* A federation-peer cross-sign directory has no backend route yet, so */
/* that shape stays local and its panel stays a genuine offline shell. */
/* ------------------------------------------------------------------ */

/** A federation peer and its cross-signature (Part III §22) — no backend
 * route exists yet, so this panel is intentionally always offline. */
interface FederationPeer {
  peer_id: string;
  display_name: string;
  /** Thumbprint of the peer's cross-signing key. */
  cross_sign_thumbprint: string;
  cross_signed: boolean;
  /** Hash of the cross-signature record committed to both logs. */
  cross_sign_hash: string;
  last_seen?: number;
}

/* ------------------------------------------------------------------ */
/* Small layout helpers (no UI kit — inline styles on CSS vars).       */
/* ------------------------------------------------------------------ */

function SectionHeading({
  eyebrow,
  title,
  children,
}: {
  eyebrow: string;
  title: string;
  children?: React.ReactNode;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "baseline",
        justifyContent: "space-between",
        gap: "12px",
        flexWrap: "wrap",
        marginBottom: "12px",
      }}
    >
      <div>
        <div
          className="mono"
          style={{
            fontSize: "11px",
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            color: "var(--indigo)",
            marginBottom: "4px",
          }}
        >
          <SpecRef text={eyebrow} />
        </div>
        <h2 style={{ fontSize: "19px", margin: 0 }}>{title}</h2>
      </div>
      {children}
    </div>
  );
}

const grid2: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(min(420px, 100%), 1fr))",
  gap: "16px",
};

/* ------------------------------------------------------------------ */
/* Inclusion-proof viewer — the centrepiece.                           */
/* Pure renderer + a live-call driver. RFC 6962 §2.1.1.                */
/* ------------------------------------------------------------------ */

const HEX64 = /^(0x)?[0-9a-fA-F]{64}$/;

type ProofPhase =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "proven"; proof: InclusionProofResult }
  | { kind: "offline"; offline: PlaneOfflineError }
  | { kind: "not-found"; message: string }
  | { kind: "error"; message: string };

/**
 * Renders the audit path as a verifiable ladder: leaf at the bottom, each
 * sibling hash a rung, up to the anchor the path climbs to. `GET
 * /v1/log/proof?leaf=` (services/trust_plane/app.py) returns
 * `{seq, anchor_date, tree_size, audit_path_hex}` — it does NOT echo a
 * recomputed root hash, so the ladder's top rung names the anchor date an
 * auditor re-derives `GET /v1/log/anchors/{anchor_date}`'s `tree_head_hash`
 * from, rather than fabricating a root this response never carried.
 */
function ProofLadder({
  proof,
  onInspect,
}: {
  proof: InclusionProofResult;
  onInspect: () => void;
}) {
  const direction = (i: number): "L" | "R" =>
    // RFC 6962: at each level, whether the sibling is left/right depends on the
    // bit of (seq >> level). Surfacing it makes the climb auditable.
    ((proof.seq >> i) & 1) === 1 ? "L" : "R";

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "0",
        marginTop: "4px",
      }}
    >
      {/* Anchor the path climbs to — the proof response does not echo a root
          hash; the anchor date is where a verifier re-derives one. */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          padding: "10px 12px",
          borderRadius: "var(--radius-sm)",
          background: "var(--teal-dim)",
          border: "1px solid rgba(var(--teal-rgb),0.3)",
        }}
      >
        <span
          className="mono"
          style={{ fontSize: "11px", color: "var(--teal)", width: "62px" }}
        >
          anchor
        </span>
        <button
          type="button"
          onClick={onInspect}
          className="mono"
          style={{
            background: "none",
            border: "none",
            padding: 0,
            fontSize: "12.5px",
            color: "var(--teal)",
            cursor: "pointer",
            textAlign: "left",
          }}
        >
          {proof.anchor_date} · tree size {proof.tree_size.toLocaleString()}{" "}
          <span aria-hidden="true" style={{ fontSize: "11px" }}>
            ↗
          </span>
        </button>
      </div>

      {proof.audit_path_hex.length === 0 ? (
        <div
          className="mono"
          style={{ fontSize: "12px", color: "var(--fg-faint)", padding: "8px 0 0 12px" }}
        >
          empty audit path — single-leaf tree; leaf hash IS the anchor's tree head.
        </div>
      ) : (
        proof.audit_path_hex
          .map((sib, i) => ({ sib, i }))
          .reverse()
          .map(({ sib, i }) => (
            <div
              key={i}
              style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}
            >
              <span
                aria-hidden="true"
                style={{
                  width: "1px",
                  height: "14px",
                  background: "var(--hair-strong)",
                  marginLeft: "31px",
                }}
              />
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  padding: "8px 12px",
                  borderRadius: "var(--radius-sm)",
                  background: "var(--bg)",
                  border: "1px solid var(--hair)",
                  width: "100%",
                  boxSizing: "border-box",
                }}
              >
                <span
                  className="mono"
                  style={{ fontSize: "11px", color: "var(--fg-faint)", width: "62px" }}
                >
                  L{i} · {direction(i)}
                </span>
                <Hash value={sib} accent="var(--indigo)" />
              </div>
            </div>
          ))
      )}

      {/* Leaf */}
      <span
        aria-hidden="true"
        style={{
          width: "1px",
          height: "14px",
          background: "var(--hair-strong)",
          marginLeft: "31px",
        }}
      />
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          padding: "10px 12px",
          borderRadius: "var(--radius-sm)",
          background: "var(--ember-dim)",
          border: "1px solid rgba(var(--ember-rgb),0.3)",
        }}
      >
        <span
          className="mono"
          style={{ fontSize: "11px", color: "var(--ember)", width: "62px" }}
        >
          leaf #{proof.seq}
        </span>
        <span className="mono" style={{ fontSize: "12px", color: "var(--fg-muted)" }}>
          tree size {proof.tree_size.toLocaleString()}
        </span>
      </div>
    </div>
  );
}

function InclusionProofViewer({
  onClaim,
  health,
}: {
  onClaim: (c: Claim) => void;
  health: PlaneHealthState;
}) {
  const [leaf, setLeaf] = useState("");
  const [phase, setPhase] = useState<ProofPhase>({ kind: "idle" });

  const valid = HEX64.test(leaf.trim());
  const touched = leaf.trim().length > 0;

  const run = useCallback(async () => {
    const value = leaf.trim();
    if (!HEX64.test(value)) return;
    setPhase({ kind: "loading" });
    try {
      const proof = await trust.getInclusionProof(value);
      setPhase({ kind: "proven", proof });
    } catch (e) {
      if (isPlaneOffline(e)) {
        setPhase({ kind: "offline", offline: e });
      } else if (isMeshApiError(e) && (e.status === 404 || e.status === 400)) {
        // 404 = leaf/anchor unknown to the log; 400 = malformed leaf address.
        // Both are the service correctly answering "no such proof" — an
        // honest not-found, never a fabricated path.
        setPhase({ kind: "not-found", message: e.message });
      } else {
        setPhase({
          kind: "error",
          message: e instanceof Error ? e.message : "Unknown error",
        });
      }
    }
  }, [leaf]);

  function onInspectProof(proof: InclusionProofResult) {
    onClaim({
      label: `Inclusion of leaf #${proof.seq} in the transparency log`,
      verdict: "attested",
      evidence: {
        hashes: proof.audit_path_hex.map((h, i) => ({
          label: `Audit path L${i}`,
          value: h,
        })),
        algorithm: "RFC 6962 §2.1.1 SHA-256 inclusion proof",
        specRef: "Part III §16.4",
        detail: [
          { label: "Leaf seq", value: String(proof.seq) },
          { label: "Anchor date", value: proof.anchor_date },
          { label: "Tree size", value: String(proof.tree_size) },
          { label: "Path length", value: String(proof.audit_path_hex.length) },
          {
            label: "note",
            value:
              "This proof names the anchor to re-derive the root from (GET /v1/log/anchors/{anchor_date}) rather than echoing one — verify by walking the path, not by trusting a badge.",
          },
        ],
        source: `trust.getInclusionProof(${leaf.trim().slice(0, 14)}…)`,
      },
    });
  }

  return (
    <div className="panel">
      <SectionHeading
        eyebrow="RFC 6962 §2.1.1 · Part III §16.4"
        title="Inclusion-proof viewer"
      />
      <p style={{ color: "var(--fg-muted)", marginTop: 0, maxWidth: "70ch" }}>
        Paste a leaf hash committed to the Merkle transparency log. The viewer
        requests its audit path and draws the climb to the signed root, so an
        auditor can replay the proof rather than trust a badge.
      </p>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          void run();
        }}
        style={{
          display: "flex",
          gap: "10px",
          flexWrap: "wrap",
          alignItems: "flex-start",
          marginTop: "8px",
        }}
      >
        <div style={{ flex: "1 1 320px", minWidth: 0 }}>
          <label htmlFor="leaf-hash" className="sr-only">
            Leaf hash (64 hex characters)
          </label>
          <input
            id="leaf-hash"
            value={leaf}
            onChange={(e) => setLeaf(e.target.value)}
            placeholder="64-hex leaf hash, e.g. a1b2c3…"
            spellCheck={false}
            autoCapitalize="off"
            autoCorrect="off"
            aria-invalid={touched && !valid}
            aria-describedby="leaf-hash-hint"
            style={{
              width: "100%",
              boxSizing: "border-box",
              minHeight: "var(--tap)",
              padding: "0 12px",
              borderRadius: "var(--radius-sm)",
              border: `1px solid ${
                touched && !valid ? "var(--red)" : "var(--hair-strong)"
              }`,
              background: "var(--bg)",
              color: "var(--fg)",
              fontFamily: "var(--font-mono)",
              fontSize: "13px",
            }}
          />
          <div
            id="leaf-hash-hint"
            className="mono"
            style={{
              fontSize: "11px",
              marginTop: "6px",
              color: touched && !valid ? "var(--red)" : "var(--fg-faint)",
            }}
          >
            {touched && !valid
              ? "Not a 64-character hex hash — proof viewer needs the raw leaf digest."
              : "SHA-256 leaf digest · 64 hex chars · 0x prefix optional"}
          </div>
        </div>
        <button
          type="submit"
          disabled={!valid || phase.kind === "loading"}
          style={{
            minHeight: "var(--tap)",
            padding: "0 18px",
            borderRadius: "var(--radius-sm)",
            border: "1px solid var(--indigo)",
            background: valid ? "var(--indigo-dim)" : "transparent",
            color: valid ? "var(--indigo)" : "var(--fg-faint)",
            fontFamily: "var(--font-mono)",
            fontSize: "13px",
            cursor: valid && phase.kind !== "loading" ? "pointer" : "not-allowed",
            opacity: phase.kind === "loading" ? 0.6 : 1,
          }}
        >
          {phase.kind === "loading" ? "Requesting…" : "Fetch proof"}
        </button>
      </form>

      <div style={{ marginTop: "18px" }}>
        {phase.kind === "idle" && (
          <p
            className="mono"
            style={{ fontSize: "12.5px", color: "var(--fg-faint)", margin: 0 }}
          >
            Awaiting a leaf hash. No proof requested yet.
          </p>
        )}

        {phase.kind === "loading" && (
          <PlaneState kind="loading" title="Requesting inclusion proof…" />
        )}

        {phase.kind === "offline" && (
          <PlaneState
            kind="offline"
            offline={phase.offline}
            serviceHealthy={health.kind === "up"}
            serviceProbing={health.kind === "probing"}
            serviceLatencyMs={health.kind === "up" ? health.latencyMs : undefined}
            title={
              health.kind === "up"
                ? "Trust service deployed — inclusion-proof API pending"
                : "Cannot prove inclusion — transparency log offline"
            }
            message={
              health.kind === "up"
                ? "The trust service is deployed and reporting healthy. The viewer is built and wired; it would draw the RFC 6962 audit path here once the proof read-API is bound. No proof is fabricated until the live endpoint answers."
                : "The viewer is built and wired; it would draw the RFC 6962 audit path here. The log service is not running, so no proof can be substantiated. The console will not fabricate a path."
            }
            endpoints={[`GET /v1/log/proof?leaf=${leaf.trim().slice(0, 12)}…`]}
            specRef="Part III §16.4"
            onRetry={() => void run()}
          />
        )}

        {phase.kind === "not-found" && (
          <PlaneState
            kind="empty"
            title="No proof for this leaf"
            message={`The log answered but has no proof for this address: ${phase.message}. Never a fabricated path — an unknown leaf or anchor is shown honestly.`}
          />
        )}

        {phase.kind === "error" && (
          <PlaneState
            kind="error"
            title="Proof request failed"
            message={phase.message}
            onRetry={() => void run()}
          />
        )}

        {phase.kind === "proven" && (
          <>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                marginBottom: "10px",
              }}
            >
              <VerifyBadge
                state="attested"
                label="Inclusion attested"
                onInspect={() => onInspectProof(phase.proof)}
              />
              <span style={{ color: "var(--fg-muted)", fontSize: "13px" }}>
                audit path of {phase.proof.audit_path_hex.length} sibling hashes
              </span>
            </div>
            <ProofLadder
              proof={phase.proof}
              onInspect={() => onInspectProof(phase.proof)}
            />
          </>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Live-data sections. Each binds to a trust.* surface; offline => the */
/* honest PlaneState panel. Ready to render real records when up.      */
/* ------------------------------------------------------------------ */

type LoadState<T> =
  | { kind: "loading" }
  | { kind: "offline"; offline: PlaneOfflineError }
  | { kind: "error"; message: string; status?: number }
  | { kind: "ready"; data: T };

function useTrustResource<T>(load: () => Promise<T>): {
  state: LoadState<T>;
  reload: () => void;
} {
  const [state, setState] = useState<LoadState<T>>({ kind: "loading" });

  const reload = useCallback(() => {
    setState({ kind: "loading" });
    load()
      .then((data) => setState({ kind: "ready", data }))
      .catch((e) => {
        if (isPlaneOffline(e)) setState({ kind: "offline", offline: e });
        else
          setState({
            kind: "error",
            message: e instanceof Error ? e.message : "Unknown error",
            status: isMeshApiError(e) ? e.status : undefined,
          });
      });
    // load identity is stable per call-site; we intentionally run once + on reload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(reload, [reload]);
  return { state, reload };
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, VerifyState> = {
    active: "verified",
    suspended: "pending",
    revoked: "failed",
  };
  return <VerifyBadge state={map[status] ?? "unknown"} label={status} size="sm" />;
}

/* ---- Signed Tree Head ---- */

function SthPanel({
  onClaim,
  health,
}: {
  onClaim: (c: Claim) => void;
  health: PlaneHealthState;
}) {
  const { state, reload } = useTrustResource<SignedTreeHead>(() =>
    trust.getSignedTreeHead(),
  );
  // 404 from GET /v1/log/sth means "no anchor published yet" — the log
  // service is up and answering correctly; this is the honest EMPTY state,
  // never an error.
  const noAnchorYet = state.kind === "error" && state.status === 404;

  return (
    <div className="panel">
      <SectionHeading
        eyebrow="RFC 6962 §3.5 · Part III §16"
        title="Signed tree head"
      >
        {state.kind === "ready" && (
          <VerifyBadge
            state="attested"
            label="STH signed"
            onInspect={() =>
              onClaim({
                label: "Signed tree head",
                verdict: "attested",
                evidence: {
                  hashes: [{ label: "Tree head hash", value: state.data.tree_head_hash }],
                  algorithm: "EdDSA (detached JWS)",
                  detail: [
                    { label: "Anchor date", value: state.data.anchor_date },
                    { label: "Tree size", value: String(state.data.tree_size) },
                    { label: "Signed at", value: state.data.signed_at },
                    { label: "Issuing CA kid", value: state.data.issuing_ca_kid },
                    { label: "Spec version", value: state.data.spec_version },
                  ],
                  specRef: "Part III §16",
                  source: "trust.getSignedTreeHead()",
                },
              })
            }
          />
        )}
      </SectionHeading>

      {state.kind === "loading" && <PlaneState kind="loading" />}
      {state.kind === "offline" && (
        <PlaneState
          kind="offline"
          offline={state.offline}
          serviceHealthy={health.kind === "up"}
          serviceProbing={health.kind === "probing"}
          serviceLatencyMs={health.kind === "up" ? health.latencyMs : undefined}
          endpoints={["GET /v1/log/sth"]}
          specRef="Part III §16"
          onRetry={reload}
        />
      )}
      {noAnchorYet && (
        <PlaneState
          kind="empty"
          title="No anchor published yet"
          message="The trust-log service is live and answered — it has not published a daily anchor yet, so there is no signed tree head to show. This is not a failure."
          onRetry={reload}
        />
      )}
      {state.kind === "error" && !noAnchorYet && (
        <PlaneState kind="error" message={state.message} onRetry={reload} />
      )}
      {state.kind === "ready" && (
        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          <Hash
            label="tree head"
            value={state.data.tree_head_hash}
            accent="var(--teal)"
          />
          <div className="mono" style={{ fontSize: "12.5px", color: "var(--fg-muted)" }}>
            tree size {state.data.tree_size.toLocaleString()} · anchor{" "}
            {state.data.anchor_date} · signed {state.data.signed_at}
          </div>
        </div>
      )}
    </div>
  );
}

/* ---- Participant registry ---- */

function RegistryPanel({
  onClaim,
  health,
}: {
  onClaim: (c: Claim) => void;
  health: PlaneHealthState;
}) {
  const { state, reload } = useTrustResource<RegistrySnapshot>(() =>
    trust.listParticipants(),
  );

  return (
    <div className="panel">
      <SectionHeading eyebrow="Part III §17 · PKI" title="Participant registry" />

      {state.kind === "loading" && <PlaneState kind="loading" />}
      {state.kind === "offline" && (
        <PlaneState
          kind="offline"
          offline={state.offline}
          serviceHealthy={health.kind === "up"}
          serviceProbing={health.kind === "probing"}
          serviceLatencyMs={health.kind === "up" ? health.latencyMs : undefined}
          endpoints={["GET /v1/registry/participants"]}
          specRef="Part III §17"
          onRetry={reload}
        />
      )}
      {state.kind === "error" && (
        <PlaneState kind="error" message={state.message} onRetry={reload} />
      )}
      {state.kind === "ready" && state.data.participants.length === 0 && (
        <PlaneState kind="empty" message="No participants registered yet." />
      )}
      {state.kind === "ready" && state.data.participants.length > 0 && (
        <ul
          style={{
            listStyle: "none",
            margin: 0,
            padding: 0,
            display: "flex",
            flexDirection: "column",
            gap: "10px",
          }}
        >
          {state.data.participants.map((p) => (
            <li
              key={p.id}
              style={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "10px",
                padding: "10px 12px",
                borderRadius: "var(--radius-sm)",
                border: "1px solid var(--hair)",
                background: "var(--bg)",
              }}
            >
              <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                <span style={{ fontSize: "14px" }}>{p.legal_name}</span>
                <button
                  type="button"
                  onClick={() =>
                    onClaim({
                      label: `${p.legal_name} — registry entry`,
                      verdict: p.status === "revoked" ? "failed" : "attested",
                      evidence: {
                        detail: [
                          { label: "Participant id", value: p.id },
                          { label: "Country", value: p.country },
                          { label: "Status", value: p.status },
                          { label: "Active keys", value: String(p.active_keys.length) },
                          { label: "Snapshot at", value: state.data.snapshot_at },
                        ],
                        specRef: "Part III §17 · §21.1",
                        source: "trust.listParticipants() — signed snapshot",
                      },
                    })
                  }
                  className="mono"
                  style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    fontSize: "12px",
                    color: "var(--gold)",
                    cursor: "pointer",
                    textAlign: "left",
                  }}
                >
                  {p.id} · {p.active_keys.length} active key
                  {p.active_keys.length === 1 ? "" : "s"}{" "}
                  <span aria-hidden="true" style={{ fontSize: "11px" }}>
                    ↗
                  </span>
                </button>
              </div>
              <StatusBadge status={p.status} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ---- Federation peers + cross-signatures ---- */

function FederationPanel({
  onClaim,
  health,
}: {
  onClaim: (c: Claim) => void;
  health: PlaneHealthState;
}) {
  const { state, reload } = useTrustResource<FederationPeer[]>(() =>
    trust.listFederationPeers() as unknown as Promise<FederationPeer[]>,
  );

  return (
    <div className="panel">
      <SectionHeading
        eyebrow="Part III §22 · federation"
        title="Federation peers & cross-signatures"
      />

      {state.kind === "loading" && <PlaneState kind="loading" />}
      {state.kind === "offline" && (
        <PlaneState
          kind="offline"
          offline={state.offline}
          serviceHealthy={health.kind === "up"}
          serviceProbing={health.kind === "probing"}
          serviceLatencyMs={health.kind === "up" ? health.latencyMs : undefined}
          endpoints={["GET /v1/federation/peers"]}
          specRef="Part III §22"
          onRetry={reload}
        />
      )}
      {state.kind === "error" && (
        <PlaneState kind="error" message={state.message} onRetry={reload} />
      )}
      {state.kind === "ready" && state.data.length === 0 && (
        <PlaneState kind="empty" message="No federation peers configured yet." />
      )}
      {state.kind === "ready" && state.data.length > 0 && (
        <ul
          style={{
            listStyle: "none",
            margin: 0,
            padding: 0,
            display: "flex",
            flexDirection: "column",
            gap: "10px",
          }}
        >
          {state.data.map((peer) => (
            <li
              key={peer.peer_id}
              style={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "10px",
                padding: "10px 12px",
                borderRadius: "var(--radius-sm)",
                border: "1px solid var(--hair)",
                background: "var(--bg)",
              }}
            >
              <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                <span style={{ fontSize: "14px" }}>{peer.display_name}</span>
                <Hash
                  label="x-sign"
                  value={peer.cross_sign_hash}
                  accent="var(--indigo)"
                />
              </div>
              <VerifyBadge
                state={peer.cross_signed ? "verified" : "unknown"}
                label={peer.cross_signed ? "Cross-signed" : "Not cross-signed"}
                size="sm"
                onInspect={
                  peer.cross_signed
                    ? () =>
                        onClaim({
                          label: `${peer.display_name} — cross-signature`,
                          verdict: "verified",
                          evidence: {
                            hashes: [
                              {
                                label: "Cross-sign record",
                                value: peer.cross_sign_hash,
                              },
                            ],
                            keyThumbprint: peer.cross_sign_thumbprint,
                            detail: [
                              { label: "Peer", value: peer.peer_id },
                              ...(peer.last_seen
                                ? [
                                    {
                                      label: "Last seen",
                                      value: new Date(
                                        peer.last_seen,
                                      ).toISOString(),
                                    },
                                  ]
                                : []),
                            ],
                            specRef: "Part III §22",
                            source: "trust.listFederationPeers()",
                          },
                        })
                    : undefined
                }
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* View                                                                */
/* ------------------------------------------------------------------ */

export default function Trust({ role }: { role: Role }) {
  const [claim, setClaim] = useState<Claim | null>(null);
  // Live health probe of the deployed trust-plane stub. When up, the offline
  // panels render "deployed · health 200 · API pending" instead of "not running".
  const { state: health } = usePlaneHealth(trust.health);

  return (
    <section>
      <header className="view-header">
        <div className="view-header__eyebrow">
          <span className="accent-teal">Trust</span>
          <span><SpecRef text="Plane III · §15–§23" /></span>
        </div>
        <h1 className="view-header__title">PKI, registry &amp; transparency log</h1>
        <p className="view-header__sub">
          RFC 6962 Merkle log, participant registry, federation cross-signing.
          Role: <strong>{role}</strong>.
        </p>
      </header>

      <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
        {/* The inclusion-proof viewer is the operator/auditor centrepiece. */}
        <InclusionProofViewer onClaim={setClaim} health={health} />

        <SthPanel onClaim={setClaim} health={health} />

        <div style={grid2}>
          <RegistryPanel onClaim={setClaim} health={health} />
          <FederationPanel onClaim={setClaim} health={health} />
        </div>
      </div>

      <ProvabilityDrawer claim={claim} onClose={() => setClaim(null)} />
    </section>
  );
}
